import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Mail, Copy, Send, Check, Trash2, MailCheck, AtSign, Languages } from "lucide-react";
import { TIPOS_RESPUESTA, RESPUESTA_ESTADOS, IDIOMAS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import MasivoBoton from "@/components/bandeja/MasivoDialog";

/** PROMPT 71: "Ver en español" -- traduce SOLO para leer. Nunca modifica el cuerpo
 * guardado del borrador (el que se manda es siempre r.cuerpo, sin tocar). */
function CuerpoRespuesta({ r }) {
  const [traduciendo, setTraduciendo] = useState(false);
  const [traduccion, setTraduccion] = useState(null);
  const [verTraduccion, setVerTraduccion] = useState(false);

  const traducirVista = async () => {
    if (traduccion) {
      setVerTraduccion((v) => !v);
      return;
    }
    setTraduciendo(true);
    try {
      const { data } = await api.post("/captura/traducir-texto", { texto: r.cuerpo, idioma_destino: "es" });
      setTraduccion(data);
      setVerTraduccion(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTraduciendo(false);
    }
  };

  const mostrarTraducido = verTraduccion && traduccion;

  return (
    <>
      <pre className="mt-2 text-[12px] leading-relaxed text-slate-700 whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded px-3 py-2 max-h-48 overflow-y-auto">
        {mostrarTraducido ? traduccion.texto_traducido : r.cuerpo}
      </pre>
      {mostrarTraducido && (
        <p className="text-[11px] text-violet-600 mt-1">Traducción automática · del {traduccion.idioma_detectado} (solo vista, el borrador se manda tal cual)</p>
      )}
      {r.idioma !== "es" && (
        <Button
          variant="ghost"
          size="sm"
          onClick={traducirVista}
          disabled={traduciendo}
          data-testid={`respuesta-ver-espanol-${r.id}`}
          className="gap-1.5 h-7 px-2 mt-1 text-[12px] text-violet-600 hover:text-violet-800 hover:bg-violet-50"
        >
          {traduciendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
          {mostrarTraducido ? "Ver original" : "Ver en español"}
        </Button>
      )}
    </>
  );
}

export default function RespuestasTab({ onCount, onChanged }) {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("borrador");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtro !== "todas" ? { estado: filtro } : {};
      const { data } = await api.get("/respuestas", { params, timeout: 20000 });
      setItems(data);
      if (filtro === "borrador") onCount?.(data.length);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filtro, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const copiar = async (r) => {
    const fromLine = r.remitente ? `De: ${r.remitente_nombre ? r.remitente_nombre + " " : ""}<${r.remitente}>\n` : "";
    const texto = `${fromLine}Para: ${r.destinatario || "(sin email)"}\nAsunto: ${r.asunto}\n\n${r.cuerpo}`;
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Respuesta copiada al portapapeles.");
    } catch {
      toast.error("No se pudo copiar automáticamente. Seleccioná y copiá el texto.");
    }
  };

  const accion = async (r, path, okMsg) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/respuestas/${r.id}/${path}`);
      if (path === "enviar" && !data.enviado) {
        toast.info(data.mensaje || "Transporte no conectado.");
      } else {
        toast.success(okMsg);
      }
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {isAdmin && <MasivoBoton modo="respuestas" refresco={items.length} onDone={() => { onChanged?.(); load(); }} />}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <p className="text-[13px] text-slate-500 max-w-2xl">
          Borradores bilingües (es/en) generados automáticamente. Cada uno sale DESDE la casilla de la marca a
          la que el cliente escribió (ver "Sale de"). Copiá el texto y enviálo desde esa casilla, o marcá como
          enviada. El envío automático se activará al conectar el transporte por cuenta (Resend / SMTP).
        </p>
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger className="w-44" data-testid="respuestas-filtro"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="borrador">Borradores</SelectItem>
            <SelectItem value="enviada">Enviadas</SelectItem>
            <SelectItem value="descartada">Descartadas</SelectItem>
            <SelectItem value="todas">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 text-sm" data-testid="respuestas-empty">
          <Mail className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          No hay respuestas {filtro === "todas" ? "" : RESPUESTA_ESTADOS[filtro]?.label?.toLowerCase()}.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const est = RESPUESTA_ESTADOS[r.estado] || RESPUESTA_ESTADOS.borrador;
            return (
              <Card key={r.id} className="p-4" data-testid={`respuesta-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-blue-50 text-blue-700 border-blue-200">
                    {TIPOS_RESPUESTA[r.tipo] || r.tipo}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-600 border-slate-200">
                    {IDIOMAS[r.idioma] || r.idioma?.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${est.cls}`}>
                    {est.label}
                  </span>
                  <span className="font-mono text-[11px] text-slate-500">{r.codigo_booking}</span>
                </div>

                <p className="text-sm font-semibold text-slate-900 mt-2">{r.asunto}</p>
                {r.remitente && (
                  <p className="text-[12px] mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium" data-testid={`respuesta-from-${r.id}`}>
                    <AtSign className="h-3.5 w-3.5" />
                    Sale de: {r.remitente_nombre ? `${r.remitente_nombre} ` : ""}&lt;{r.remitente}&gt;
                    {r.marca_nombre && <span className="text-emerald-500/80">· {r.marca_nombre}</span>}
                  </p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">
                  Para: {r.destinatario || "(sin email del cliente — completá el contacto)"}
                </p>
                <CuerpoRespuesta r={r} />

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Button variant="outline" size="sm" onClick={() => copiar(r)} data-testid={`respuesta-copiar-${r.id}`} className="gap-1.5">
                    <Copy className="h-4 w-4" /> Copiar
                  </Button>
                  {r.estado === "borrador" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => accion(r, "enviar", "Enviada.")} disabled={busyId === r.id} data-testid={`respuesta-enviar-${r.id}`} className="gap-1.5">
                        <Send className="h-4 w-4" /> Enviar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => accion(r, "marcar-enviada", "Marcada como enviada.")} disabled={busyId === r.id} data-testid={`respuesta-marcar-${r.id}`} className="gap-1.5">
                        <MailCheck className="h-4 w-4" /> Marcar enviada
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => accion(r, "descartar", "Descartada.")} disabled={busyId === r.id} data-testid={`respuesta-descartar-${r.id}`} className="gap-1.5 text-slate-500 hover:text-red-600 ml-auto">
                        <Trash2 className="h-4 w-4" /> Descartar
                      </Button>
                    </>
                  )}
                  {r.estado === "enviada" && (
                    <span className="inline-flex items-center gap-1 text-[12px] text-emerald-600 ml-auto">
                      <Check className="h-4 w-4" /> Enviada ({r.canal_envio === "manual" ? "manual" : "automático"})
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
